import RefreshIcon from '@mui/icons-material/Refresh'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material'
import * as React from 'react'
import { useTranslate } from 'react-admin'
import { useVersionCheck } from '../hooks/useVersionCheck'
import { hardRefresh } from '../utils/hardRefresh'

const COUNTDOWN_SECONDS = 60

/**
 * Blocking escalation of UpdateAvailableBanner: shown when the running
 * bundle is long-stale (>24h) or hit the operator force_before kill-switch
 * (see useVersionCheck). No dismiss, no backdrop-close — the page reloads
 * when the countdown ends or the user clicks Reload now.
 *
 * Why blocking is justified here (the banner's "never auto-reload" note
 * still holds for FRESH staleness): a 13-day-stale bundle with a since-fixed
 * infinite refetch loop DoS'd prod logins on 2026-07-09. Past 24h of ignored
 * nudges, the risk of running known-buggy code outweighs the cost of an
 * interruption — and the 60s countdown still gives the vet a moment to
 * finish a note. Unsaved form input IS lost on reload; that is the accepted
 * trade, which is why the escalation waits 24h rather than firing on the
 * first stale poll.
 */
export const ForceReloadDialog: React.FC = () => {
  const translate = useTranslate()
  const { forceReload, deployedSha } = useVersionCheck()
  const [secondsLeft, setSecondsLeft] = React.useState(COUNTDOWN_SECONDS)
  const firedRef = React.useRef(false)

  React.useEffect(() => {
    if (!forceReload) return
    const interval = window.setInterval(() => {
      setSecondsLeft((prev) => (prev > 0 ? prev - 1 : 0))
    }, 1000)
    return () => window.clearInterval(interval)
  }, [forceReload])

  React.useEffect(() => {
    if (forceReload && secondsLeft === 0 && !firedRef.current) {
      firedRef.current = true
      void hardRefresh(deployedSha)
    }
  }, [forceReload, secondsLeft, deployedSha])

  const handleReloadNow = () => {
    if (firedRef.current) return
    firedRef.current = true
    void hardRefresh(deployedSha)
  }

  return (
    <Dialog open={forceReload} disableEscapeKeyDown maxWidth="xs" fullWidth>
      <DialogTitle>{translate('pos.app_update.dialog_title')}</DialogTitle>
      <DialogContent>
        <DialogContentText>{translate('pos.app_update.dialog_body')}</DialogContentText>
        <DialogContentText sx={{ mt: 2, fontWeight: 'bold' }}>
          {translate('pos.app_update.reloading_in', { seconds: secondsLeft })}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button variant="contained" startIcon={<RefreshIcon />} onClick={handleReloadNow}>
          {translate('pos.app_update.reload_now')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default ForceReloadDialog
